'use client';
import { getSessionId } from '@/lib/trackerSession'; // one tracker id, created if missing (lead ↔ visit, Founder 2026-09-27)
// Direct-contact engine (2026-08-04) — removes the wa-first "before contacting"
// intermediate popup (ContactModal's intro/revealed steps) for the common case: a
// click now fires the whole flow synchronously instead of gating behind an extra
// confirmation screen. ContactModal.tsx itself is untouched, kept as a reference
// implementation of the same popup-gated pattern (not currently rendered anywhere live).
//
// 2026-08-04 CORRECTION (same day, user-caught live production bug): an EARLIER
// version of this file sent the visitor straight to the professional (wa.me/<pro> or
// tel:<pro>) — the user immediately flagged this as a serious bug: "contact must NEVER
// go directly to the business owner, only ever to OUR WhatsApp (972506403800), which
// then hands back tracked /wa/<code> or /tel/<code> links." Every path below now
// creates the tracked link(s) and hands them to the COMPANY WhatsApp message — never
// navigates the visitor to the pro directly, on any device, for any intent.
import { SMART_INTAKE_KEY } from '@/components/SmartIntakeBar';
import { openCompanyWhatsApp } from './companyWa';
import { fireWaConfirm } from './contactTracking';
import { logInquiry } from './inquiries';
import { currentGreeting } from './eveningClient';
// @ts-ignore — plain-JS composer shared with scripts/wa-worker.mjs (Founder 2026-09-28: Hebrew-correct lead texts)
import { visitorLeadMessage, proLeadMessage } from './leadMessage.mjs';

function readSessionIds() {
  let sessionId: string | null = null;
  try {
    const saved = localStorage.getItem(SMART_INTAKE_KEY);
    if (saved) sessionId = JSON.parse(saved)?.sessionId ?? null;
  } catch {}
  let trackerSessionId: string | null = null;
  try { trackerSessionId = getSessionId(); } catch {}
  return { sessionId, trackerSessionId };
}

/** Creates one he_wa_links row (kind 'wa' or 'tel') and returns its tracked URL, or
 * null if the request failed. */
async function createTrackedContactLink(opts: {
  proId: number; kind: 'wa' | 'tel'; message?: string; categorySlug?: string | null; city?: string | null;
}): Promise<string | null> {
  const { sessionId, trackerSessionId } = readSessionIds();
  try {
    const res = await fetch('/api/wa-links/single', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        proId: opts.proId, kind: opts.kind, message: opts.message || '',
        categorySlug: opts.categorySlug ?? null, city: opts.city ?? null, sessionId, trackerSessionId,
      }),
    });
    const data = await res.json();
    return data?.ok ? data.url : null;
  } catch { return null; }
}

/**
 * The ONLY way any contact button should reach a professional: build tracked
 * /tel/<code> and/or /wa/<code> link(s) (per `intent`) and open OUR company WhatsApp
 * with a message containing them — the visitor sends that message to US, sees the
 * link(s) in their own WhatsApp, and clicks through from there. Never navigates the
 * visitor to the pro directly. Returns the URL(s) it managed to create (best-effort —
 * still opens company WhatsApp with plain instructions even if link creation failed).
 */
export async function contactViaCompany(opts: {
  proId: number; displayName: string; proGender?: string | null; categorySlug?: string | null; categoryName?: string | null;
  intent: 'call' | 'whatsapp' | 'default';
  ticket?: string | null; // an inquiry ALREADY logged for this send (the questionnaire's) — reuse it, never a second ticket (bughunter round 3 #4)
  city?: string | null; // the visitor's city as used in the message (already gated per pro) — stored on the inquiry (2026-09-28)
  waMessage?: string; // pro-facing message text for the wa link, if intent includes 'whatsapp'
  need?: string | null; // the questionnaire's detail phrase (cleanNeed) — becomes "לגבי …" in Hebrew-correct form (lib/leadMessage.mjs)
}): Promise<{ telUrl: string | null; waUrl: string | null }> {
  // NEW FLOW (Founder 2026-09-25): the visitor sends ONE short message with a ticket number to the company number
  // (no links); the WhatsApp worker replies with the expert's card (photo + WhatsApp/phone buttons). The tracked
  // /wa and /tel rows are still created (tied to the ticket) — they become the card's buttons and the click tracking.
  if (process.env.NEXT_PUBLIC_WA_NEW_FLOW === '1') {
    // ROOT FIX (Founder 12/10/26, פנייה 14065 / pro 3308): the questionnaire's real need (opts.need) was used ONLY for the
    // texts sent right now — never persisted on the ticket row — so every LATER reader (wa-worker's cards_sent / lead text,
    // needFromInquiryRow) found an empty questionnaire_answers and fell back to the generic placeholder "עבודה". Carrying
    // it into logInquiry's questionnaireAnswers (object shape { cleanNeed } — the same shape needFromInquiryRow reads)
    // means the real phrase ("תיקון מזגן" etc.) survives for every downstream consumer, not just this one message.
    const ticket = opts.ticket || await logInquiry({ source: 'floating_button', categorySlug: opts.categorySlug || undefined, proId: opts.proId, messageText: `direct:${opts.intent}`, city: opts.city || undefined, questionnaireAnswers: opts.need ? { cleanNeed: opts.need } : undefined });
    // LAW 04/10/26: the pro-facing text is ALWAYS the one builder's (lib/heMsg.mjs via proLeadMessage) — the legacy waMessage
    // ("שלום X,\nפונה אלייך…") was gendered and could address a business; it is kept only for the wa-confirm log.
    const proText = (proLeadMessage({ proName: opts.displayName, need: opts.need, city: opts.city, categoryName: opts.categoryName }) as string);
    const { sessionId, trackerSessionId } = readSessionIds();
    const mk = (kind: 'wa' | 'tel') => fetch('/api/wa-links/single', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ proId: opts.proId, kind, message: kind === 'wa' ? proText : '', categorySlug: opts.categorySlug ?? null, city: opts.city ?? null, sessionId, trackerSessionId, inquiryTicket: ticket }) }).then(r => r.json()).catch(() => null);
    const [w, t] = await Promise.all([mk('wa'), mk('tel')]);
    // Founder 2026-09-25: short, gender-neutral, name + subject bold (WhatsApp renders *…* when the visitor sends it)
    // Founder 2026-09-28 (the spec): "אשמח לדבר עם ליאור (אינסטלטור)" — the profession in parentheses, never after "לגבי";
    // "לגבי <details>" only when the visitor gave details (plain text — WA-cards law 2026-09-27: no *bold* in prefills).
    // ROOT FIX (Founder 12/10/26, פנייה 14065): the CALL door (intent==='call') hides the visible "קוד: N" — invisible
    // clickMark only (lib/heMsg.mjs hideClickCode) — the WhatsApp/default doors are unchanged.
    openCompanyWhatsApp(visitorLeadMessage({ proName: opts.displayName, proGender: opts.proGender === 'f' ? 'f' : null, categoryName: opts.categoryName, need: opts.need, city: opts.city, ticket, hideClickCode: opts.intent === 'call', greeting: currentGreeting() }) as string);
    fireWaConfirm({ proId: opts.proId, proName: opts.displayName, categorySlug: opts.categorySlug ?? undefined, categoryName: opts.categoryName, intent: opts.intent, waMessage: opts.waMessage || null });
    return { telUrl: t?.ok ? t.url : null, waUrl: w?.ok ? w.url : null };
  }
  const needTel = opts.intent !== 'whatsapp';
  const needWa = opts.intent !== 'call';
  const [telUrl, waUrl] = await Promise.all([
    needTel ? createTrackedContactLink({ proId: opts.proId, kind: 'tel', categorySlug: opts.categorySlug, city: opts.city }) : Promise.resolve(null),
    needWa ? createTrackedContactLink({ proId: opts.proId, kind: 'wa', message: opts.waMessage || '', categorySlug: opts.categorySlug, city: opts.city }) : Promise.resolve(null),
  ]);

  // 2026-08-04 (user request): include the category so the message reads
  // "לשליחת פנייה לגיל - טכנאי דוד שמש" instead of just the bare name — useful once
  // the admin has several open threads with different pros in view at once.
  const who = opts.categoryName ? `${opts.displayName} - ${opts.categoryName}` : opts.displayName;
  // 2026-08-23 (user request): number the two separate actions explicitly — sending
  // this message here is action 1, clicking the link inside it (after it's sent) is a
  // SEPARATE action 2 — visitors were treating the message itself as the whole
  // contact, not realizing a second tap on the link is what actually reaches the pro.
  // Kept the rest of the existing copy untouched, just prefixed/clarified these two
  // lines everywhere this template appears.
  // 2026-08-23 (user request, round 2): name the pro explicitly in the action-2 line
  // too, not just the opening line — "לחץ על הקישור בכדי לשלוח את ההודעה לאיילין"
  // instead of a generic "ישירות", so it's unambiguous who the click actually reaches.
  // 2026-08-23 (user request, round 3): the opening line said "לשליחת פנייה" (sending
  // an inquiry) even for the pure call-only flow, which reads oddly once step 2 is
  // clearly "בכדי להתקשר" — a phone call isn't really "an inquiry", it's a call.
  // Only the call-only branch gets a call-specific opening; the other two (which
  // genuinely do end in a WhatsApp message) keep "לשליחת פנייה".
  const openingLine = telUrl && !waUrl ? `בכדי להתקשר ל${who}` : `לשליחת פנייה ל${who}`;
  const lines = [openingLine, '', '1) שלח את ההודעה'];
  if (telUrl && waUrl) {
    lines.push('2) לאחר ששלחת, לחץ על הקישור בכדי:', `📞 להתקשר ל${opts.displayName} — ${telUrl}`, `💬 לשלוח את ההודעה ל${opts.displayName} — ${waUrl}`);
  } else if (telUrl) {
    lines.push(`2) לאחר ששלחת, לחץ על הקישור בכדי להתקשר ל${opts.displayName}:`, '', telUrl);
  } else if (waUrl) {
    lines.push(`2) לאחר ששלחת, לחץ על הקישור בכדי לשלוח את ההודעה ל${opts.displayName}:`, '', waUrl);
  }
  openCompanyWhatsApp(lines.join('\n'));

  // 2026-08-15 fix (user report, lead traced via /admin/wa-links → he_wa_links.code):
  // the 2026-08-04 rewrite that introduced contactViaCompany() as the single choke
  // point for every contact button dropped the he_contact_events "real conversion"
  // log that used to fire here (fireWaConfirm — see contactTracking.ts). Result: from
  // 2026-08-04 to today, he_contact_events logged ~0 rows/day while he_wa_links (real
  // clicks) kept logging hundreds/day — /admin/contacts showed no real leads, and the
  // Google Ads conversion-upload pipeline (reads click_confirmed_at/ads_uploaded_at
  // from the same table) had no real conversions to upload for 11 days.
  fireWaConfirm({
    proId: opts.proId, proName: opts.displayName, categorySlug: opts.categorySlug ?? undefined,
    categoryName: opts.categoryName, intent: opts.intent, waMessage: opts.waMessage || null,
  });

  return { telUrl, waUrl };
}
